import { remove } from 'lodash-es';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, inject } from '@angular/core';
import { ClientReferralTask } from '../../../core/models/client-referral-task.model';
import { Prescription } from '../../../core/models/prescription.model';
import { ClientReferralVerification } from '../../../core/models/client-referral-verification.model';
import {
  ClientReferralVerificationsService,
  PagedClientReferralVerifications,
} from '../../../core/services/client-referral-verifications.service';
import { PagedPrescriptions, PrescriptionsService } from '../../../core/services/prescriptions.service';
import { ClientReferralTaskChoice } from '../../../core/enums/client-referral-task-choice.enum';
import { Sort } from '../../../core/models/paged-results/limit-offset-paging.model';
import { ClientReferralTaskOptionsComponent } from '../client-referral-task-options/client-referral-task-options.component';

import { ClientReferralTaskDatesComponent } from '../client-referral-task-dates/client-referral-task-dates.component';
import { ClientReferralTaskPrescriptionsComponent } from '../client-referral-task-prescriptions/client-referral-task-prescriptions.component';
import { ClientReferralTaskActionsComponent } from '../client-referral-task-actions/client-referral-task-actions.component';

const VerificationsLimit = 10;

@Component({
    selector: 'app-client-referral-task-verification',
    templateUrl: './client-referral-task-verification.component.html',
    styleUrls: ['./client-referral-task-verification.component.scss'],
    imports: [ClientReferralTaskOptionsComponent,
              ClientReferralTaskDatesComponent,
              ClientReferralTaskPrescriptionsComponent,
              ClientReferralTaskActionsComponent]
})
export class ClientReferralTaskVerificationComponent implements OnInit, OnChanges {
  private verificationsService = inject(ClientReferralVerificationsService);
  private prescriptionsService = inject(PrescriptionsService);

  isYesSelected = false;
  loadingVerifications = true;
  loadingPrescriptions = true;
  showPrescriptionsWarning = false;
  canAddVerifications = true;
  verifications: ClientReferralVerification[] = new Array<ClientReferralVerification>();
  prescriptions: Prescription[] = new Array<Prescription>();

  @Input() task: ClientReferralTask;
  @Input() formDisabled: boolean;
  @Output() taskChange = new EventEmitter<ClientReferralTask>();
  @Output() prescriptionDismissed = new EventEmitter();

  ngOnInit() {
    this.init();
  }

  ngOnChanges() {
    this.init();
  }

  onTaskChange(task: ClientReferralTask) {
    this.taskChange.emit(task);
  }

  onAddVerification() {
    this.verificationsService.create(this.task.patientPrescriberId).subscribe(verification => {
      const verifications = [...this.verifications];
      verifications.push(verification);
      this.verifications = verifications;
      this.updateCanAddVerifications();
      this.loadPrescriptions();
    });
  }

  onUpdateVerification(updated: { id: number; referralDate: string }) {
    this.verificationsService.update(updated.id, updated.referralDate).subscribe(v => {
      const index = this.verifications.findIndex(verification => verification.id === v.id);
      const verifications = [...this.verifications];

      if (index > -1) {
        verifications[index] = v;
      }

      this.verifications = verifications;
      this.loadPrescriptions();
    });
  }

  onRemoveVerification(id: number) {
    this.verificationsService.destroy(id).subscribe(() => {
      const verifications = [...this.verifications];

      remove(verifications, v => v.id === id);

      this.verifications = verifications;

      this.updateCanAddVerifications();
      this.loadPrescriptions();
    });
  }

  private init() {
    this.isYesSelected = this.task.choice === ClientReferralTaskChoice.yes;
    this.loadVerifications();
    this.loadPrescriptions();
  }

  private loadVerifications() {
    this.loadingVerifications = true;

    this.verificationsService
      .getList({ patientPrescriberId: this.task.patientPrescriberId })
      .subscribe((result: PagedClientReferralVerifications) => {
        this.verifications = result.clientReferralVerifications;
        this.updateCanAddVerifications();
        this.loadingVerifications = false;
      });
  }

  private loadPrescriptions() {
    this.loadingPrescriptions = true;

    const sort = new Sort();
    sort.col = 'written_date';
    sort.dir = 'desc';

    this.prescriptionsService
      .getList({ patientPrescriberId: this.task.patientPrescriberId }, sort, { pageSize: 'all' })
      .subscribe((result: PagedPrescriptions) => {
        this.prescriptions = result.prescriptions;
        this.showPrescriptionsWarning =
          this.isYesSelected && this.prescriptions.some(p => p.anyClientReferralVerificationsInvalid);
        this.loadingPrescriptions = false;
      });
  }

  private updateCanAddVerifications() {
    this.canAddVerifications = this.verifications.length < VerificationsLimit;
  }
}
